import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { ToasterService } from 'angular2-toaster';
import { AiSettingsService } from '../../../services/ai-settings.service';
import {
  AI_SNAPSHOT_MODEL_OPTIONS,
  AI_SNAPSHOT_PROMPT_MAX_LENGTH,
  DEFAULT_AI_SNAPSHOT_MODEL,
  DEFAULT_AI_SNAPSHOT_PROMPT
} from '../../../constants/ai-snapshot-prompt';

@Component({
  selector: 'app-modal-ai-settings',
  templateUrl: './modal-ai-settings.component.html',
  styleUrls: ['./modal-ai-settings.component.scss']
})
export class ModalAiSettingsComponent implements OnInit {
  prompt = '';
  model = DEFAULT_AI_SNAPSHOT_MODEL;
  originalPrompt = '';
  originalModel = DEFAULT_AI_SNAPSHOT_MODEL;
  modelOptions = [...AI_SNAPSHOT_MODEL_OPTIONS];
  maxLength = AI_SNAPSHOT_PROMPT_MAX_LENGTH;
  updatedAt: string | null = null;
  savedToDatabase = false;
  loadFailed = false;
  isLoading = true;
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<ModalAiSettingsComponent>,
    private aiSettingsService: AiSettingsService,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.loadSettings();
  }

  get isDirty(): boolean {
    return (
      this.prompt.trim() !== this.originalPrompt.trim() ||
      this.model !== this.originalModel
    );
  }

  get isTooLong(): boolean {
    return this.prompt.length > this.maxLength;
  }

  get isModelAllowed(): boolean {
    return AI_SNAPSHOT_MODEL_OPTIONS.indexOf(this.model) >= 0;
  }

  get canSave(): boolean {
    return (
      !this.isSaving &&
      !this.isLoading &&
      this.prompt.trim().length > 0 &&
      !!this.model &&
      this.isModelAllowed &&
      !this.isTooLong &&
      this.isDirty
    );
  }

  get statusSourceLabel(): string {
    if (this.loadFailed) {
      return 'Client defaults (API unavailable)';
    }

    if (this.savedToDatabase) {
      return 'Saved in database';
    }

    return 'Server defaults (not saved to database yet)';
  }

  loadSettings() {
    this.isLoading = true;
    this.loadFailed = false;

    this.aiSettingsService.getAiSnapshotSettings().subscribe(
      state => {
        this.prompt = state.prompt;
        this.model = state.model;
        this.originalPrompt = state.prompt;
        this.originalModel = state.model;
        this.ensureModelOption(state.model);
        this.updatedAt = state.updatedAt || null;
        this.savedToDatabase = state.savedToDatabase;
        this.isLoading = false;
      },
      err => {
        this.isLoading = false;

        if (err && err.status === 403) {
          this.toasterService.popAsync(
            'error',
            'Access denied',
            this.aiSettingsService.parseApiError(err, 'Only administrators can edit AI prompt settings.')
          );
          this.closeDialog();
          return;
        }

        if (err && err.status === 401) {
          this.toasterService.popAsync(
            'error',
            'Authorization required',
            this.aiSettingsService.parseApiError(err, 'Please log in again.')
          );
          this.closeDialog();
          return;
        }

        this.prompt = DEFAULT_AI_SNAPSHOT_PROMPT;
        this.model = DEFAULT_AI_SNAPSHOT_MODEL;
        this.originalPrompt = DEFAULT_AI_SNAPSHOT_PROMPT;
        this.originalModel = DEFAULT_AI_SNAPSHOT_MODEL;
        this.savedToDatabase = false;
        this.loadFailed = true;
        this.toasterService.popAsync(
          'warning',
          'Load failed',
          this.aiSettingsService.parseApiError(err, 'Using client defaults until the API is available.')
        );
      }
    );
  }

  saveSettings() {
    if (!this.canSave) {
      return;
    }

    this.isSaving = true;

    this.aiSettingsService.saveAiSnapshotSettings(this.prompt.trim(), this.model).subscribe(
      dto => {
        this.originalPrompt = this.prompt.trim();
        this.originalModel = this.model;
        this.updatedAt = (dto && dto.updatedAt) || new Date().toISOString();
        this.savedToDatabase = true;
        this.loadFailed = false;
        this.isSaving = false;
        this.toasterService.popAsync('success', 'Saved', 'AI prompt settings updated successfully.');
      },
      error => {
        this.isSaving = false;

        if (error && error.status === 403) {
          this.toasterService.popAsync('error', 'Access denied', 'Only administrators can save AI prompt settings.');
          return;
        }

        this.toasterService.popAsync(
          'error',
          'Error',
          this.aiSettingsService.parseApiError(error, 'Failed to save AI prompt settings.')
        );
      }
    );
  }

  resetToDefault() {
    this.prompt = DEFAULT_AI_SNAPSHOT_PROMPT;
    this.model = DEFAULT_AI_SNAPSHOT_MODEL;
  }

  private ensureModelOption(model: string) {
    if (model && this.modelOptions.indexOf(model) === -1) {
      this.modelOptions = [model, ...this.modelOptions];
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  formatUpdatedAt(value: string | null): string {
    if (!value) {
      return 'Not saved yet';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/modals/modal-ai-settings/modal-ai-settings.component.ts